/** @jsx isolateComponent */

import React, { useEffect, useRef, useState } from 'react';
import type { Ref } from 'react';
import type { Interpolation } from '@emotion/css';
import styled from '@emotion/styled';
import { Link, Colors, Icon } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { NavDropdown } from './NavDropdown';
import { Submenu as SubmenuLinks } from './Submenu';

import type { NavItemType, NavSize, OnChooseItem } from './types';

export type NavRefElement = HTMLDivElement;

export type Props = {
  /**
   * Optional label that provides context as to what the navigation is used for.
   * Used by screen reading software.
   */
  ariaLabel?: string;
  /**
   * React ref that is forwarded along as a prop to facilitate
   * typing and to eliminate use of `forwardRef`.
   */
  forwardedRef?: Ref<NavRefElement>;
  /**
   * An array of items to render within the navigation.
   */
  items: Array<NavItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * The `href` of the currently selected navigation item. `href` is
   * used rather than the full item since the item object could be
   * cloned, which ruins `===` checks.
   */
  selectedItemHref: string;
  /**
   * Optional value that will determine if the nav item should be highlighted
   * or not. When not provided, the `href` will be compared to the
   * `selectedItemHref` passed to `Nav`.
   */
  isItemSelected: (item: NavItemType, selectedItemHref: string) => boolean;
  /**
   * Size of navigation. `medium` typically used for small screens
   * and `large` typically used for larger screens.
   */
  size: NavSize;
  /**
   * Whether or not the component should render the grey indicator track beneath the nav items.
   */
  shouldRenderTrack: boolean;
  /**
   * Whether or not underline the selected item in the navigation.
   */
  underlineSelectedItem: boolean;
  /**
   * Custom Link component to render in the Navigation items.
   */
  linkComponent?: React.ElementType;
};

const indicatorTrackHeight = 3;

const Styles = labelStyles('Nav', {
  root: (props: Props) => [
    {
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      columnGap: 20,
      rowGap: 2,
      alignItems: 'center',
    },

    props.size === 'medium' &&
      withModifier('medium', {
        height: 50,
        fontSize: 14,
      }),

    props.size === 'large' &&
      withModifier('large', {
        height: 70,
        fontSize: 16,
      }),
  ],

  primary: ({ count }: { count: number }) => [
    {
      display: 'grid',
      height: '100%',
      // Allow shrinking
      minWidth: 0,
      maxWidth: '100%',
    },

    withModifier(`count-${count}`, {
      // `minmax(auto, min-content)` behaves such that at the min value of
      // `auto` items could truncate if necessary, and at the max value of
      // `min-content` items will not render any wider than they need to,
      // ensuring that they are left-aligned rather than stretching to fill
      // any leftover space.
      gridTemplateColumns: `repeat(${count}, minmax(auto, min-content))`,
      gap: 20,
    }),
  ],

  item: ({
    isSelected,
    isParentSectionLink = false,
  }: {
    isSelected: boolean;
    isParentSectionLink: boolean;
  }) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 10px',
      textAlign: 'center',
      color: Colors.grey700,
      lineHeight: '20px',
      fontWeight: 600,

      '&:hover, &:focus': {
        color: Colors.blue500,
      },
    },
    isSelected &&
      withModifier('selected', {
        color: Colors.black,
        fontWeight: 700,
      }),
    isParentSectionLink &&
      withModifier('parent-link', {
        padding: 0,
      }),
  ],

  itemText: {
    display: 'block',
    whiteSpace: 'nowrap',
  },

  indicatorTrack: (props: Props) => [
    {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      // This ends up being a non-integer, but it actually renders better
      // in Chrome (and still renders fine in other browsers) this way.
      borderRadius: indicatorTrackHeight / 2,
      height: indicatorTrackHeight,
      overflow: 'hidden',
    },
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    props.shouldRenderTrack &&
      withModifier('visible', {
        backgroundColor: Colors.grey100,
      }),
  ],

  indicator: (props: Props) => [
    {
      position: 'absolute',
      height: 'inherit',
      borderRadius: 'inherit',
      backgroundColor: props.underlineSelectedItem ? Colors.blue500 : 'initial',
      transition: 'all 250ms ease-in-out',
    },
  ],
});

type NavItemRefElement = HTMLAnchorElement | HTMLDivElement;

type NavItemProps = NavItemType & {
  onChoose: (event: React.MouseEvent) => void;
  forwardedRef: Ref<NavItemRefElement> | null;
  isSelected: boolean;
};

// Since this is a component rather than a DOM node, `forwardRef`
// is used to facilitate properly passing the `ref` along to the DOM node.
const NavItem = (props: NavItemProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const openMenu = () => setIsMenuOpen(true);
  const closeMenu = () => setIsMenuOpen(false);

  // I was getting a `JSX element type Elem does not have any construct or call signatures` Typescript error.
  // Fixed it with: https://stackoverflow.com/a/60323737/1405803
  const LinkComponent = props.linkComponent as React.ElementType;

  /**
   * Render a regular link as it doesn't have any children
   */
  if (!props.submenu?.length) {
    return (
      <LinkComponent
        ariaLabel={props.isSelected ? `${props.text}, selected` : props.text}
        css={
          Styles.item({
            isSelected: props.isSelected,
          }) as Interpolation
        }
        href={props.href}
        onClick={props.onChoose}
        ref={props.forwardedRef as Ref<HTMLAnchorElement>}
      >
        <ItemText>{props.text}</ItemText>
      </LinkComponent>
    );
  }

  /**
   * Render a link with a Submenu
   */
  return (
    <Details onMouseEnter={openMenu} onMouseLeave={closeMenu} open={isMenuOpen}>
      <Summary aria-haspopup="menu" role="button" tabIndex={0}>
        <SummaryInnerText css={Styles.summaryInner}>
          <ItemText>{props.text}</ItemText>
          <Caret>
            <Icon isBlock={true} name="arrowSmallDown" size={16} />
          </Caret>
        </SummaryInnerText>
      </Summary>
      <Submenu role="menu">
        <SubmenuLinks links={props.submenu} />
      </Submenu>
    </Details>
  );
};

/**
 * Creates a horizontal navigation component that can display a variable
 * number of items, and allows excess items to be nested into a dropdown menu.
 */
export const Nav = (props: Props) => {
  // Hold onto ref so the selected node can be measured
  // in order to position `indicator` via `indicatorStyles`.
  const selectedItemRef = useRef<NavItemRefElement>(null);
  const [indicatorStyles, setIndicatorStyles] = useState({});

  const shouldRenderIndicator = !props.items.every((i) => i.isExtra);

  const primaryItems = props.items.filter((item) => !item.isExtra);
  const primaryLinks = primaryItems.map((item) => {
    const isSelected = props.isItemSelected(item, props.selectedItemHref);
    return (
      <NavItem
        {...item}
        forwardedRef={
          isSelected && shouldRenderIndicator ? selectedItemRef : null
        }
        isSelected={isSelected}
        key={item.text}
        onChoose={(event) => props.onChooseItem(item, event)}
        linkComponent={props.linkComponent}
      />
    );
  });

  const secondaryItems = props.items.filter((item) => item.isExtra);
  const isSecondaryItemSelected = secondaryItems.some((item) => {
    return props.isItemSelected(item, props.selectedItemHref);
  });
  const secondaryMenu = secondaryItems.length > 0 && (
    <div
      ref={
        isSecondaryItemSelected && shouldRenderIndicator
          ? (selectedItemRef as Ref<HTMLDivElement>)
          : null
      }
    >
      <NavDropdown
        items={secondaryItems}
        onChooseItem={props.onChooseItem}
        selectedItemHref={props.selectedItemHref}
        shouldIndicateSelected={shouldRenderIndicator}
        size={props.size}
      />
    </div>
  );

  // Whenever `selectedItemHref` changes, read the position of
  // the `current`ly selected node.
  useEffect(() => {
    // `requestAnimationFrame` ensures rendering has completed
    // and measuring is accurate. Without this some measurements
    // may be off on initial render.
    requestAnimationFrame(() => {
      setIndicatorStyles(
        selectedItemRef.current
          ? {
              left: selectedItemRef.current!.offsetLeft,
              width: selectedItemRef.current!.offsetWidth,
            }
          : {},
      );
    });
  }, [props.items, props.selectedItemHref]);

  return (
    <nav aria-label={props.ariaLabel} css={Styles.root(props) as Interpolation}>
      <div
        css={Styles.primary({ count: primaryLinks.length }) as Interpolation}
        ref={props.forwardedRef}
      >
        {primaryLinks}
      </div>
      {secondaryMenu}
      {props.shouldRenderTrack && (
        <div css={Styles.indicatorTrack(props) as Interpolation}>
          <div
            css={Styles.indicator(props) as Interpolation}
            style={indicatorStyles}
          />
        </div>
      )}
    </nav>
  );
};

Nav.defaultProps = {
  isItemSelected: (item: NavItemType, selectedItemHref: string) =>
    item.href === selectedItemHref,
  onChooseItem: () => {},
  shouldRenderTrack: true,
  size: 'medium',
  /**
   * We default to ZDS Link component
   */
  linkComponent: Link,
};

const Caret = styled.span`
  color: ${Colors.grey700};
  margin-left: 3px;
`;

const Summary = styled.summary`
  height: 100%;
  list-style-type: none;
  /* Hides default icon on details element */
  &::-webkit-details-marker {
    display: none;
  }
  /* Hide default icon from IE. */
  &::before {
    display: none;
  }
`;

const Submenu = styled.div`
  width: 220px;
  position: absolute;
  left: 0;
  top: 100%;
  /**
    This node needs to be at exactly "top: 100%" so that there isn't
    a gap of even 1px between it and its toggle, otherwise hovering
    off can occur in that gap and cause the menu to hide. However visually
    the node needs to be pushed down 1px so that the topbar's bottom border
    renders. Therefore we need a transparent 1px sliver that: 1) is part of
    the node and thus fills the 1px gap caused by the topbar's bottom border,
    2) is transparent so the topbar's bottom border can still be seen,
    even though it's overlapped by the node, and 3) push the content
    within this node down by 1px so that its background doesn't overlap
    the topbar's bottom border and cover it up. So there you have it,
    one line of code with a 10 line explanation 😄
  */
  border-top: 1px solid transparent;
  /**
    Pad wrapper around Submenu to clip its shadow only on the top edge.
    The shadow needs to be clipped because it's not (reasonably) possible
    to have the node render beneath the topbar when it's a child of the topbar.
    The padding values are based on the offset/blur/spread of the shadow.
    This serves a dual purpose of also increasing the hover area of the
    menu so that hovering a little bit off of it doesn't close the menu.
  */
  padding: 0 20px 20px 20px;
  /** Need to reposition the menu due to the transparent padding. */
  margin-left: -20px;
  /** Actually clip the shadow, again using padding as a sort of clip mask. */
  overflow: hidden;
  /**
  Padding should add to the width of the box in this case since it
  isn't visual. Makes it a little easier to reason about dimensions.
  */
  box-sizing: content-box;
`;

const SummaryInnerText = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
`;

const ItemText = styled.span`
  color: ${Colors.grey700};
  font-size: 14px;
  font-weight: 600;
  display: block;
  white-space: nowrap;
  &:hover,
  :focus,
  :focus-within {
    color: ${Colors.blue500};
  }
`;

const Details = styled.details`
  display: flex;
  position: relative;
  &:hover,
  :focus,
  :focus-within {
    & ${ItemText}, ${Caret} {
      color: ${Colors.blue500};
    }
  }
  &:hover {
    cursor: default;
  }
`;
